import {
  ArrowDropDownOutlined,
  AssignmentOutlined,
  EventBusyOutlined,
  LocalShippingOutlined,
  WarningAmberOutlined
} from "@mui/icons-material";
import { Button, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import { MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";

export type HomeDashboardView = "operations" | "stock-risk" | "stock-risk-v2" | "replenishment" | "expiry-risk";

interface HomeDashboardSelectorProps {
  fullWidth?: boolean;
  value: HomeDashboardView;
  onChange: (value: HomeDashboardView) => void;
  operationsLabel?: string;
  showStockRisk?: boolean;
  showStockRiskV2?: boolean;
  showReplenishment?: boolean;
  showExpiryRisk?: boolean;
}

export function HomeDashboardSelector({
  value, onChange, operationsLabel, fullWidth = false, showStockRiskV2 = false,
  showReplenishment = true, showExpiryRisk = false
}: HomeDashboardSelectorProps) {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const select = (next: HomeDashboardView) => {
    setAnchor(null);
    onChange(next);
  };

  return <>
    <Button fullWidth={fullWidth} variant={fullWidth ? "outlined" : "text"} size="small" color="inherit" endIcon={<ArrowDropDownOutlined />}
      aria-haspopup="menu" aria-expanded={Boolean(anchor)}
      onClick={(event: MouseEvent<HTMLButtonElement>) => setAnchor(event.currentTarget)}
      sx={{ fontWeight: 850, px: 1, minWidth: 0, minHeight: { xs: 44, sm: 32 },
        whiteSpace: "normal", textAlign: "left", justifyContent: fullWidth ? "space-between" : "center",
        "& .MuiButton-endIcon": { flexShrink: 0 } }}>
      {value === "stock-risk" ? t("dashboard.views.stockRisk")
        : value === "stock-risk-v2" ? t("dashboard.views.stockRiskV2")
        : value === "replenishment" ? t("dashboard.views.replenishment")
        : value === "expiry-risk" ? t("dashboard.views.expiryRisk")
          : operationsLabel ?? t("dashboard.views.operations")}
    </Button>
    <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
      slotProps={{ paper: { sx: { maxWidth: "calc(100vw - 32px)",
        "& .MuiMenuItem-root": { whiteSpace: "normal", minHeight: 44 },
        "& .MuiListItemText-root": { minWidth: 0, overflowWrap: "anywhere" } } } }}>
      <MenuItem selected={value === "operations"} onClick={() => select("operations")}>
        <ListItemIcon><AssignmentOutlined fontSize="small" /></ListItemIcon>
        <ListItemText>{operationsLabel ?? t("dashboard.views.operations")}</ListItemText>
      </MenuItem>
      {showStockRiskV2 && <MenuItem selected={value === "stock-risk-v2"} onClick={() => select("stock-risk-v2")}>
        <ListItemIcon><WarningAmberOutlined fontSize="small" color="warning" /></ListItemIcon>
        <ListItemText>{t("dashboard.views.stockRiskV2")}</ListItemText>
      </MenuItem>}
      {showReplenishment && <MenuItem selected={value === "replenishment"} onClick={() => select("replenishment")}>
        <ListItemIcon><LocalShippingOutlined fontSize="small" /></ListItemIcon>
        <ListItemText>{t("dashboard.views.replenishment")}</ListItemText>
      </MenuItem>}
      {showExpiryRisk && <MenuItem selected={value === "expiry-risk"} onClick={() => select("expiry-risk")}>
        <ListItemIcon><EventBusyOutlined fontSize="small" color="warning" /></ListItemIcon>
        <ListItemText>{t("dashboard.views.expiryRisk")}</ListItemText>
      </MenuItem>}
    </Menu>
  </>;
}
